// Endpoints tipados del backend SurveyPro. Agrupa los contratos de la API.
import { api } from "./client";

export interface Project { id: number; name: string; }
export interface OdkForm { xmlFormId: string; name: string; }
export interface VariableEntry {
  name: string; label: string | null; type: string | null;
  list_name: string | null; choices: Record<string, string> | null;
  is_technical?: boolean;
}

export const OdkAPI = {
  projects: () => api.get<Project[]>("/api/odk/projects"),
  forms: (projectId: number) => api.get<OdkForm[]>(`/api/odk/forms/${projectId}`),
  columns: (projectId: number, formId: string) =>
    api.get<string[]>(`/api/odk/columns/${projectId}/${formId}`),
  variables: (projectId: number, formId: string) =>
    api.get<VariableEntry[]>(`/api/forms/${projectId}/${formId}/variables`),
  // Lo que este usuario puede HACER con la encuesta. Sirve para no ofrecer un
  // botón que va a devolver 403; el corte de verdad está en cada endpoint.
  acceso: (projectId: number, formId: string) =>
    api.get<AccesoEncuesta>(`/api/forms/${projectId}/${formId}/acceso`),
};

export interface AccesoEncuesta {
  variables_permitidas: string[] | null;
  puede_descargar_diccionario: boolean;
}

export const AnalysisAPI = {
  descriptives: (b: {
    project_id: number; form_id: string; variables: string[]; cross_var?: string | null;
  }) => api.post<Record<string, unknown>>("/api/analysis/descriptives", b),
  regression: (b: {
    project_id: number; form_id: string; y_col: string; x_cols: string[];
    model_type: string; dummy_var?: string | null; dummy_val?: string | null;
    standardize_x?: boolean;
  }) => api.post<Record<string, unknown>>("/api/analysis/regression", b),
  bootstrap: (b: {
    project_id: number; form_id: string; target_var: string;
    n_samples: number; sample_size: number; dummy_val?: string | null; method?: string;
  }) => api.post<Record<string, unknown>>("/api/analysis/bootstrap", b),
  forecast: (b: {
    project_id: number; form_ids: string[]; value_col: string;
    periods: number; model: string;
  }) => api.post<Record<string, unknown>>("/api/analysis/forecast", b),
  // Proyectar: la serie se construye igual que en «Construir serie» (varias
  // encuestas, olas o variable de ola) y se pide un resultado por variable.
  forecastMulti: (b: {
    project_id: number; form_ids: string[]; value_cols: string[];
    periods: number; freq: string; mode?: string; wave_var?: string | null;
    models?: string[] | null;
  }) => api.post<MultiResultado<ForecastResult>>("/api/analysis/forecast-multi", b),
  // Serie de una o varias variables NUMÉRICAS: un resultado por variable.
  compare: (b: {
    project_id: number; form_ids: string[]; value_cols: string[]; freq: string;
    mode?: string; wave_var?: string | null;
  }) => api.post<MultiResultado<NumericSeriesResult>>("/api/analysis/compare", b),
  // Serie de una o varias variables CATEGÓRICAS: el reparto de sus categorías en
  // el tiempo. Una lista de candidatos no tiene promedio; lo que evoluciona es el
  // % de cada uno.
  compareCategorical: (b: {
    project_id: number; form_ids: string[]; value_cols: string[]; freq: string;
    mode?: string; wave_var?: string | null;
    max_categorias?: number | null; min_n?: number | null; base?: string | null;
  }) => api.post<MultiResultado<CategoricalSeriesResult>>("/api/analysis/compare-categorical", b),
  // La configuración de IA es global y vive en el panel: aquí ya no viajan
  // endpoint ni token. Exige project_id/form_id porque comprueba permisos.
  ai: (b: { project_id: number; form_id: string; data_summary: unknown }) =>
    api.post<{
      analysis: string; modelo: string;
      tokens_entrada: number; tokens_salida: number; costo: number;
    }>("/api/analysis/ai", b),
  descriptiveReport: (b: { project_id: number; form_id: string; variables: string[] }) =>
    api.post<Record<string, DescriptiveEntry>>("/api/analysis/descriptive-report", b),
  poststratify: (b: {
    project_id: number; form_id: string; strata_var: string;
    population: Record<string, number>; target_var?: string | null;
  }) => api.post<PoststratifyResult>("/api/analysis/poststratify", b),
};

/**
 * Respuesta de los endpoints de series: un resultado por variable pedida, en el
 * mismo orden. `error` arriba solo cuando no se pudo construir NADA (sin
 * encuestas, sin variables, variable de ola inexistente); el fallo de una sola
 * variable va dentro de su propio resultado.
 */
export interface MultiResultado<T> {
  error?: string;
  value_cols?: string[];
  resultados?: (T & { value_col: string; error?: string })[];
  n_variables?: number;
  n_con_error?: number;
}

export interface NumericSeries {
  label: string; dates: string[]; values: number[]; n: number;
  point_labels?: string[]; point_ns?: number[];
}
export interface NumericSeriesResult {
  error?: string;
  value_col?: string; freq?: string; mode?: string; wave_var?: string;
  series?: NumericSeries[];
  n_series?: number;
}

export interface ForecastModel {
  /** Nombre que se enseña: «ARIMA(1,1,0)», «Holt-Winters»… */
  name: string;
  /** Clave con la que se pidió: «ARIMA», «Holt-Winters», «ETS», «Tendencia lineal». */
  clave: string;
  aic: number;
  fitted: number[];
  forecast: number[];
  forecast_dates: string[];
  ci_lower: number[] | null;
  ci_upper: number[] | null;
  orden?: number[];
  pendiente?: number;
  /** Acierto fuera de muestra sobre los últimos `h_prueba` puntos. `null` = no se pudo medir. */
  mae: number | null;
  mape: number | null;
  prueba_error?: string;
  prueba_reales?: number[];
  prueba_pred?: number[];
}
export interface ForecastResult {
  error?: string;
  value_col?: string;
  mode?: string;
  freq?: string | null;
  /** false = serie de olas: el eje es la ola y el futuro va en «+1, +2…». */
  regular?: boolean;
  historical?: { dates: string[]; values: number[]; point_labels?: string[] | null; nota?: string };
  models?: ForecastModel[];
  best_model?: string;
  best_aic?: number;
  best_mae?: number | null;
  criterio?: "mae" | "aic";
  h_prueba?: number;
  /** Modelo → por qué no pudo ajustarse. Siempre presente. */
  errors?: Record<string, string>;
  n_periodos?: number;
}

export interface CategoricalPeriod {
  label: string;
  date: string | null;
  n: number;
  n_validas: number;
  n_sin_respuesta: number;
  /** false = tenía menos respuestas que el mínimo: el punto no se dibuja. */
  suficiente: boolean;
}
export interface CategoricalSeries {
  label: string;
  dates: string[];
  point_labels: string[];
  /** Porcentaje por período. `null` donde no había n suficiente. */
  values: (number | null)[];
  counts: number[];
  moes: (number | null)[];
  n: number;
}
export interface CategoricalSeriesResult {
  error?: string;
  value_col?: string;
  kind?: "categorical";
  base?: "validas" | "todas";
  eje?: "fecha" | "etiqueta";
  min_n?: number;
  mode?: string;
  freq?: string | null;
  wave_var?: string | null;
  periodos?: CategoricalPeriod[];
  categorias?: string[];
  series?: CategoricalSeries[];
  n_periodos?: number;
  n_categorias?: number;
  avisos?: string[];
}

export interface DescriptiveCategory { n: number; pct: number; moe: number; }
export interface DescriptiveEntry {
  error?: string;
  label?: string;
  type?: string | null;
  kind?: "categorical" | "numeric";
  n_valid?: number;
  n_missing?: number;
  n_total?: number;
  categories?: Record<string, DescriptiveCategory>;
  n_categories?: number;
  choices?: Record<string, string> | null;
  stats?: Record<string, number>;
}

export interface PoststratifyWeight {
  n_sample: number; pct_sample: number; pct_population: number | null; weight: number | null;
}
export interface PoststratifyResult {
  error?: string;
  strata_var?: string;
  n_total?: number;
  effective_n?: number;
  weights?: Record<string, PoststratifyWeight>;
  weighted_target?: {
    variable: string;
    categories: Record<string, { n: number; weighted_pct: number; unweighted_pct: number }>;
  };
  unrepresented_in_population?: string[];
  population_missing_in_sample?: string[];
}

// ── Preguntas abiertas ───────────────────────────────────────────────────────

export interface OpenQuestion {
  name: string;
  label: string | null;
  type: string | null;
  n_total: number;
  n_respondidas: number;
  en_los_datos: boolean;
}

export interface OpenOptions {
  estrategia?: string;
  max_respuestas_por_llamada?: number;
  tam_lote?: number;
  tam_muestra?: number;
  semilla?: number;
  min_chars_respuesta?: number;
  max_chars_respuesta?: number;
  min_n_segmento?: number;
  n_categorias_objetivo?: number;
  idioma?: string;
}

export interface OpenRequest {
  project_id: number;
  form_id: string;
  questions: string[];
  cross_vars?: string[];
  fecha_desde?: string | null;
  fecha_hasta?: string | null;
  opciones?: OpenOptions;
  lectura_transversal?: boolean;
}

export interface OpenEstimateItem {
  pregunta: string;
  etiqueta: string;
  estrategia: string;
  n_disponibles: number;
  n_a_analizar: number;
  n_segmentos: number;
  caracteres: number;
  tokens_entrada_aprox: number;
  llamadas: number;
  lotes: number;
  costo_estimado: number;
  descartes: { vacias: number; descartadas: number; recortadas: number };
}

export interface OpenEstimate {
  configurada: boolean;
  estrategia: string;
  preguntas: OpenEstimateItem[];
  llamadas_totales: number;
  costo_estimado_total: number;
}

export interface OpenTema { tema: string; peso: number; ejemplos?: string[] }
export interface OpenCategoria { nombre: string; definicion?: string; pct?: number }
export interface OpenSegmento { resumen?: string; temas?: string[]; diferencias?: string }

export interface OpenResultado {
  resumen_general?: string;
  temas?: OpenTema[];
  tendencias?: string[];
  categorias?: OpenCategoria[];
  citas_representativas?: string[];
  por_segmento?: Record<string, OpenSegmento>;
  comparacion_entre_segmentos?: string;
  advertencias?: string[];
  _meta?: {
    estrategia: string; n_disponibles: number; n_analizadas: number;
    n_segmentos: number; n_lotes: number;
    descartes: { vacias: number; descartadas: number; recortadas: number };
    fecha_desde: string | null; fecha_hasta: string | null;
    variables_cruce: string[];
  };
}

export interface OpenAnalysis {
  id: number;
  project_id: number;
  form_id: string;
  question_name: string;
  question_label: string | null;
  cross_vars: string[];
  filtros: Record<string, string | null>;
  estrategia: string;
  n_total: number;
  n_analizadas: number;
  n_lotes: number;
  prompt_clave: string | null;
  prompt_sistema?: string | null;
  prompt_usuario?: string | null;
  resultado: OpenResultado | null;
  status: string;
  error: string | null;
  tokens_in: number;
  tokens_out: number;
  costo: number;
  duracion_ms: number;
  created_at: string | null;
  /** De qué análisis es complemento. null = es un análisis original. */
  parent_id?: number | null;
  /** Lo que se pidió al complementar, textual. */
  instruccion_extra?: string | null;
  es_complemento?: boolean;
  complementos?: {
    id: number;
    instruccion: string | null;
    status: string;
    created_at: string | null;
  }[];
}

export interface OpenRunResult {
  analisis: OpenAnalysis[];
  lectura_transversal: Record<string, unknown> | null;
  fallos: { pregunta: string; etiqueta: string; error: string }[];
}

export const OpenTextAPI = {
  questions: (projectId: number, formId: string) =>
    api.get<OpenQuestion[]>(`/api/forms/${projectId}/${formId}/open-questions`),
  estimate: (b: OpenRequest) => api.post<OpenEstimate>("/api/analysis/open-text/estimate", b),
  run: (b: OpenRequest) => api.post<OpenRunResult>("/api/analysis/open-text", b),
  history: (projectId: number, formId: string, question?: string) =>
    api.get<OpenAnalysis[]>(
      `/api/analysis/open-text?project_id=${projectId}&form_id=${encodeURIComponent(formId)}` +
        (question ? `&question=${encodeURIComponent(question)}` : "")
    ),
  get: (id: number) => api.get<OpenAnalysis>(`/api/analysis/open-text/${id}`),
  remove: (id: number) => api.del(`/api/analysis/open-text/${id}`),
  /** Amplía un análisis ya guardado sin volver a hacerlo (ni a pagarlo). */
  complementar: (id: number, instruccion: string) =>
    api.post<OpenAnalysis>(`/api/analysis/open-text/${id}/complementar`, { instruccion }),
  pdf: (b: { analysis_id: number; title?: string; subtitle?: string }) =>
    api.postBlob("/api/export/open-text.pdf", b),
};

// ── Configuración y prompts ──────────────────────────────────────────────────

export interface CampoSchema {
  titulo: string;
  tipo: "texto" | "texto_largo" | "entero" | "decimal" | "bool" | "enum" | "json" | "secreto";
  descripcion?: string;
  default?: unknown;
  opciones?: string[];
  min?: number;
  max?: number;
  env?: string;
}

export interface GrupoSchema {
  titulo: string;
  descripcion: string;
  campos: Record<string, CampoSchema>;
}

export interface SettingsPayload {
  schema: Record<string, GrupoSchema>;
  valores: Record<string, Record<string, unknown>>;
  origenes: Record<string, Record<string, "panel" | "env" | "fabrica">>;
  marcador_secreto: string;
}

export interface PruebaIA {
  ok: boolean;
  proveedor: string;
  modelo: string;
  tokens_entrada: number;
  tokens_salida: number;
  tokens_desde_cache: number;
  tokens_razonamiento: number;
  latencia_ms: number;
  reintentado_sin_razonar: boolean;
  respuesta: unknown;
}

export interface UsoIA {
  configurada: boolean;
  llamadas: {
    id: number; tarea: string; prompt_clave: string | null;
    proveedor: string; modelo: string;
    tokens_entrada: number; tokens_salida: number; tokens_cache: number;
    tokens_razonamiento: number; costo: number; latencia_ms: number;
    truncado: boolean; error: string | null; ref: string | null; created_at: string | null;
  }[];
  por_tarea: {
    tarea: string; proveedor: string; modelo: string; llamadas: number;
    tokens_entrada: number; tokens_salida: number; tokens_cache: number;
    tokens_razonamiento: number; pct_cache: number; costo: number;
  }[];
}

export interface DiagnosticoCache {
  tokens_aprox: number;
  variables_en_sistema: string[];
  cacheable: boolean;
  motivos: string[];
}

export interface PromptEntry {
  clave: string;
  titulo: string;
  grupo: string;
  descripcion: string;
  variables: string[];
  default: { sistema: string; usuario: string };
  vigente: { sistema: string; usuario: string };
  personalizado: boolean;
  cache: DiagnosticoCache;
}

export const SettingsAPI = {
  all: () => api.get<SettingsPayload>("/api/admin/settings"),
  save: (grupo: string, valores: Record<string, unknown>) =>
    api.put<{ status: string; valores: Record<string, unknown>;
              origenes: Record<string, "panel" | "env" | "fabrica"> }>(
      `/api/admin/settings/${grupo}`,
      { valores }
    ),
  probarIA: () => api.post<PruebaIA>("/api/admin/ia/probar", {}),
  usoIA: () => api.get<UsoIA>("/api/admin/ia/uso"),
  probarAuditor: () => api.post<PruebaAuditor>("/api/admin/auditor/probar", {}),
  invalidarCacheAuditor: () =>
    api.post<{ status: string; mensaje: string }>("/api/admin/auditor/invalidar-cache", {}),
};

/** Lo que devuelve la prueba de lectura contra el Auditor de Encuestas. */
export interface PruebaAuditor {
  ok: boolean;
  mensaje: string;
  llave_de_cruce: string;
  encuestas: {
    encuesta_id: number;
    nombre: string;
    odk_project_id: number;
    odk_form_id: string;
    con_transcripcion: number;
    caracteres: number;
  }[];
}

// ── Bitácora de errores ──────────────────────────────────────────────────────

export interface ErrorRegistrado {
  id: number;
  origen: string;
  mensaje: string;
  detalle: string | null;
  visto: boolean;
  user_email: string | null;
  created_at: string | null;
}

export interface BitacoraErrores {
  pendientes: number;
  total: number;
  por_origen: { origen: string; veces: number; ultimo: string | null }[];
  errores: ErrorRegistrado[];
}

export const ErroresAPI = {
  list: (soloNoVistos = false, limite = 100) =>
    api.get<BitacoraErrores>(
      `/api/admin/errores?solo_no_vistos=${soloNoVistos}&limite=${limite}`
    ),
  pendientes: () => api.get<{ pendientes: number }>("/api/admin/errores/pendientes"),
  marcarVisto: (id: number) => api.post(`/api/admin/errores/${id}/visto`, {}),
  marcarTodosVistos: (origen?: string) =>
    api.post(`/api/admin/errores/vistos${origen ? `?origen=${encodeURIComponent(origen)}` : ""}`, {}),
  borrar: (id: number) => api.del(`/api/admin/errores/${id}`),
  purgar: (dias?: number) =>
    api.del<{ status: string; borrados: number; dias: number }>(
      `/api/admin/errores/purgar${dias ? `?dias=${dias}` : ""}`
    ),
};

export const PromptsAPI = {
  list: () => api.get<PromptEntry[]>("/api/admin/prompts"),
  save: (clave: string, sistema: string, usuario: string) =>
    api.put<{ status: string; personalizado: boolean; mensaje: string; cache: DiagnosticoCache }>(
      `/api/admin/prompts/${clave}`,
      { sistema, usuario }
    ),
  diagnostico: (sistema: string) =>
    api.post<DiagnosticoCache>("/api/admin/prompts/diagnostico", { sistema }),
};

export interface PanelUser {
  id: number;
  email: string;
  nombre: string | null;
  is_active: boolean;
  role: string;
  debe_cambiar_password: boolean;
  last_login: string | null;
  created_at: string | null;
}

export const AdminAPI = {
  users: () => api.get<PanelUser[]>("/api/users"),
  // `password_generada` solo llega en la respuesta del alta o del reinicio: no se
  // guarda en claro en ninguna parte, así que hay que enseñarla en ese momento.
  createUser: (b: { email: string; nombre?: string; rol: string; password?: string }) =>
    api.post<PanelUser & { password_generada?: string }>("/api/users", b),
  updateUser: (userId: number, b: { nombre?: string; rol?: string; is_active?: boolean }) =>
    api.patch<PanelUser>(`/api/users/${userId}`, b),
  resetPassword: (userId: number) =>
    api.post<{ status: string; email: string; password_generada: string }>(
      `/api/users/${userId}/reset-password`, {}
    ),
  deleteUser: (userId: number) => api.del(`/api/users/${userId}`),
  toggleUser: (userId: number) => api.post(`/api/users/${userId}/toggle`, {}),
  setRole: (userId: number, role_name: string) =>
    api.post(`/api/users/${userId}/role`, { role_name }),
  listPermissions: (userId?: number) =>
    api.get<PermisoEncuesta[]>(
      userId ? `/api/admin/permissions?user_id=${userId}` : "/api/admin/permissions"
    ),
  upsertPermission: (b: {
    user_id: number; project_id: number; form_id: string; allowed_variables?: string[] | null;
    puede_descargar_diccionario?: boolean | null;
  }) => api.post<{ status: string; permission_id: number; puede_descargar_diccionario: boolean }>(
    "/api/admin/permissions", b
  ),
  deletePermission: (id: number) => api.del(`/api/admin/permissions/${id}`),
  invalidateCache: (projectId: number, formId: string) =>
    api.post(`/api/admin/cache/invalidate/${projectId}/${formId}`, {}),
};

export interface PermisoEncuesta {
  id: number;
  user_id: number;
  project_id: number;
  form_id: string;
  allowed_variables: string[] | null;
  /** Descargar el diccionario en Excel es llevarse el cuestionario entero. */
  puede_descargar_diccionario: boolean;
}

export const ExportsAPI = {
  dataExcelUrl: (projectId: number, formId: string) =>
    `/api/export/data/${projectId}/${formId}.xlsx`,
  dictionaryExcelUrl: (projectId: number, formId: string) =>
    `/api/export/dictionary/${projectId}/${formId}.xlsx`,
  reportPdf: (b: {
    title: string; subtitle?: string; metrics?: Record<string, unknown>;
    table?: string[][]; narrative?: string;
  }) => api.postBlob("/api/export/report.pdf", b),
  descriptivePdf: (b: {
    project_id: number; form_id: string; variables: string[];
    title: string; subtitle?: string; narrative?: string;
  }) => api.postBlob("/api/export/descriptive-report.pdf", b),
};

// ── Acumulados y fusiones de encuestas (v3.7.0) ─────────────────────────────

export interface DatasetMapping {
  target: string;
  label?: string | null;
  categories?: Record<string, string>;
  labels?: Record<string, string>;
}

export interface DatasetSource {
  project_id: number;
  form_id: string;
  wave: string;
  included?: boolean;
  mappings?: Record<string, DatasetMapping>;
  excluded_variables?: string[];
  coding_release_id?: number | null;
}

export interface DatasetDefinition {
  sources: DatasetSource[];
  automatic?: boolean;
  interval_minutes?: number;
  exclusions?: Record<string, string>;
  grants?: Record<string, string[]>;
}

export interface AccumulatedDatasetItem {
  id: number;
  name: string;
  project_id: number;
  generation: number;
  current_revision_id: number | null;
  form_id: string | null;
  n: number;
  updated_at: string | null;
  job?: { id: number; status: string; progress: Record<string, unknown>; error: string | null } | null;
  definition?: DatasetDefinition;
  manifest?: { definition: DatasetDefinition; dictionary: VariableEntry[]; sources: unknown[]; n: number } | null;
  revisions?: { id: number; created_at: string; n: number; form_id: string }[];
  audit?: { action: string; detail: unknown; created_at: string }[];
}

export const DatasetsAPI = {
  list: () => api.get<AccumulatedDatasetItem[]>("/api/datasets"),
  get: (id: number) => api.get<AccumulatedDatasetItem>(`/api/datasets/${id}`),
  create: (b: { name: string; project_id: number; definition: DatasetDefinition }) =>
    api.post<AccumulatedDatasetItem>("/api/datasets", b),
  update: (id: number, b: { name: string; project_id: number; definition: DatasetDefinition; expected_generation?: number }) =>
    api.put<AccumulatedDatasetItem>(`/api/datasets/${id}`, b),
  refresh: (id: number) => api.post<AccumulatedDatasetItem>(`/api/datasets/${id}/refresh`),
  cases: (id: number, offset = 0, limit = 50) =>
    api.get<{ total: number; cases: { key: string; source: string; wave: string; date: string }[] }>(`/api/datasets/${id}/cases?offset=${offset}&limit=${limit}`),
};

// ── Mapas territoriales y análisis espacial GPS (v3.7.0) ───────────────────

export interface GeoFeature {
  type: "Feature";
  geometry: { type: string; coordinates: unknown };
  properties: Record<string, unknown>;
}

export interface GeoFeatureCollection {
  type: "FeatureCollection";
  features: GeoFeature[];
  counts: Record<string, number>;
  mode: string;
  numeric: boolean;
  range?: [number, number] | null;
  categories?: Record<string, number>;
  choices?: Record<string, string>;
  warning?: string | null;
  weighting?: string;
  note?: string;
}

export interface GeoRequest {
  project_id: number;
  form_id: string;
  gps_field?: string;
  latitude_field?: string;
  longitude_field?: string;
  variable?: string;
  mode?: "points" | "grid" | "density" | "change";
  category?: string | null;
  filters?: Record<string, string[]>;
  waves?: string[];
  date_from?: string | null;
  date_to?: string | null;
  bbox?: [number, number, number, number] | null;
  grid_meters?: number;
  min_n?: number;
  weight?: string;
  equal_waves?: boolean;
  offset?: number;
  limit?: number;
}

export interface GeoConfig {
  options: {
    gps_field: string;
    latitude_field: string;
    longitude_field: string;
    grid_meters: number;
    min_n: number;
    point_limit: number;
    tiles_url: string;
    attribution: string;
    colors: Record<string, string>;
    numeric_low: string;
    numeric_high: string;
    cache_minutes: number;
  };
  can_exact: boolean;
  can_configure: boolean;
}

export const GeoAPI = {
  fields: (projectId: number, formId: string) =>
    api.get<{ dictionary: VariableEntry[]; gps_fields: string[]; n: number; waves: string[] }>(`/api/geo/fields/${projectId}/${encodeURIComponent(formId)}`),
  query: (b: GeoRequest) => api.post<GeoFeatureCollection>("/api/geo/query", b),
  cases: (b: GeoRequest) => api.post<{ total: number; counts: Record<string, number>; cases: unknown[] }>("/api/geo/cases", b),
  config: (projectId: number, formId: string) =>
    api.get<GeoConfig>(`/api/geo/config/${projectId}/${encodeURIComponent(formId)}`),
  saveConfig: (projectId: number, formId: string, values: Partial<GeoConfig["options"]>) =>
    api.patch<{ status: string; options: GeoConfig["options"] }>(`/api/geo/config/${projectId}/${encodeURIComponent(formId)}`, values),
};


// ── Visor de exit poll ───────────────────────────────────────────────
export interface ExitPollPunto {
  codigo: string; nombre: string; lat: number | null; lon: number | null;
  electores: number | null; meta: number | null; sector?: string; encuestadores: string[];
}
export interface ExitPollOptions {
  campo_punto: string; campo_encuestador: string; campo_hora: string;
  campo_aceptacion: string; valor_rechazo: string; campos_voto: string[]; campo_gps: string;
  campo_sexo: string; campo_edad: string;
  cortes: string[]; ponderacion: "local" | "electores" | "ninguna"; zona_horaria: string;
  hora_apertura: number; hora_cierre: number;
}
export interface ExitPollConfig {
  options: ExitPollOptions; puntos: ExitPollPunto[]; puede_configurar: boolean; actualizado: string | null;
}
export interface ExitPollEstimacion { n: number; pct: Record<string, number>; margen: number | null; }
export interface ExitPollVariable {
  campo: string; categorias: string[]; sin_respuesta: number;
  por_hora: (ExitPollEstimacion & { hora: string })[];
  acumulado: (ExitPollEstimacion & { hasta: string })[];
  cortes: (ExitPollEstimacion & { corte: string; disponible: boolean })[];
  por_sexo: (ExitPollEstimacion & { sexo: string })[];
}
export interface ExitPollAvancePunto {
  codigo: string; nombre: string; sector: string; lat: number | null; lon: number | null;
  electores: number | null; meta: number | null; efectivas: number; rechazos: number;
  aceptacion: number | null; avance: number | null; ultima: string | null; minutos_sin_envio: number | null;
  encuestadores_asignados: string[]; encuestadores_vistos: string[]; por_hora: Record<string, number>; alertas: string[];
}
export interface ExitPollAvanceEncuestador {
  encuestador: string; punto_asignado: string | null; punto_asignado_nombre: string | null;
  puntos_registrados: string[]; efectivas: number; rechazos: number; aceptacion: number | null;
  ritmo_hora: number | null; primera: string | null; ultima: string | null; minutos_sin_envio: number | null;
  distancia_mediana_m: number | null; por_hora: Record<string, number>; alertas: string[];
}
export interface ExitPollResultados {
  ahora: string; leido_odk: string; cache_segundos: number;
  resumen: {
    efectivas: number; rechazos: number; aceptacion: number | null; meta_total: number | null;
    avance: number | null; puntos_con_datos: number; puntos_total: number; sin_hora: number;
    fuera_de_muestra: Record<string, number>; encuestadores_activos: number;
  };
  variables: ExitPollVariable[];
  por_punto: ExitPollAvancePunto[];
  por_encuestador: ExitPollAvanceEncuestador[];
  perfil: { sexo: ExitPollPerfil[]; edad: ExitPollPerfil[] };
}
export interface ExitPollPerfil {
  categoria: string; efectivas: number; rechazos: number; aceptacion: number | null; pct_efectivas: number | null;
}

export const ExitPollAPI = {
  config: (projectId: number, formId: string) =>
    api.get<ExitPollConfig>(`/api/exitpoll/config/${projectId}/${encodeURIComponent(formId)}`),
  guardar: (projectId: number, formId: string, body: { options?: Partial<ExitPollOptions>; puntos?: ExitPollPunto[] }) =>
    api.put<{ status: string; options: ExitPollOptions; puntos: ExitPollPunto[] }>(
      `/api/exitpoll/config/${projectId}/${encodeURIComponent(formId)}`, body),
  leerPuntos: (csv: string) =>
    api.post<{ puntos: ExitPollPunto[]; n: number; sin_coordenadas: string[]; encuestadores: number }>(
      "/api/exitpoll/puntos/leer", { csv }),
  resultados: (projectId: number, formId: string, refrescar = false) =>
    api.get<ExitPollResultados>(
      `/api/exitpoll/resultados/${projectId}/${encodeURIComponent(formId)}${refrescar ? "?refrescar=true" : ""}`),
};

// ───────────────────────────────────────────── Conteo rápido
export interface ConteoMesa { mesa: string; tipo: "titular" | "reserva" | "padron"; reserva_de: string; local: string; zona: string; sector?: string; }
export interface ConteoOptions {
  modo: string; campo_mesa: string; campo_total: string; campo_fuente: string; prefijo_votos: string;
  total_mesas: number; diferencia: string[]; zona_horaria: string; min_votos?: number;
}
export interface ConteoConfig { options: ConteoOptions; mesas: ConteoMesa[]; puede_configurar: boolean; actualizado: string | null; }
export interface ConteoFila {
  codigo: string; partido: string; votos: number;
  pct_emitidos: number | null; me_emitidos: number | null; pct_validos: number | null; me_validos: number | null;
}
export interface ConteoActa {
  mesa: string; tipo: string; en_muestra: boolean; local: string; zona: string; sector: string; hora: string | null; fuente: string; foto: boolean;
  total: number; sospechosa: boolean; [partido: string]: string | number | boolean | null;
}
export interface ConteoDiferencia { a: string; b: string; diferencia_validos: number | null; margen: number | null; empate_tecnico: boolean; }
export interface ConteoSector { sector: string; zona: string; actas: number; validos: number; pct: Record<string, number | null>; }
export interface ConteoVista {
  resultados: ConteoFila[];
  diferencia: ConteoDiferencia | null;
  evolucion: Array<{ actas: number; hora: string | null; [partido: string]: number | string | null }>;
  zonas: Array<{ zona: string; titulares: number; recibidas: number }>;
  sectores: ConteoSector[];
}
export interface ConteoResultados extends ConteoVista {
  leido_odk: string; cache_segundos: number;
  resumen: {
    actas: number; titulares: number; avance: number | null; votos: number; total_mesas: number; sospechosas: number; min_votos: number;
    fuera_de_muestra: number; votos_fuera_de_muestra: number; todas_actas: number; todas_votos: number; calculado: string;
  };
  partidos: Record<string, string>;
  todas: ConteoVista;
  mesas: ConteoActa[];
  pendientes: Array<{ mesa: string; local: string; zona: string }>;
  fuera_de_muestra: Array<{ mesa: string; total: number }>;
}

export const ConteoAPI = {
  config: (projectId: number, formId: string) =>
    api.get<ConteoConfig>(`/api/conteo/config/${projectId}/${encodeURIComponent(formId)}`),
  guardar: (projectId: number, formId: string, body: { options?: Partial<ConteoOptions>; mesas?: ConteoMesa[] }) =>
    api.put<{ status: string; options: ConteoOptions; mesas: ConteoMesa[] }>(
      `/api/conteo/config/${projectId}/${encodeURIComponent(formId)}`, body),
  leerMuestra: (csv: string) =>
    api.post<{ mesas: ConteoMesa[]; titulares: number; reservas: number }>("/api/conteo/muestra/leer", { csv }),
  resultados: (projectId: number, formId: string, refrescar = false) =>
    api.get<ConteoResultados>(
      `/api/conteo/resultados/${projectId}/${encodeURIComponent(formId)}${refrescar ? "?refrescar=true" : ""}`),
};
